import type { TFunction } from 'i18next'

import type {
  ShipmentActivityFeedItem,
  ShipmentContractFeedItem,
  ShipmentEmailFeedItem,
  ShipmentFeedItem,
  ShipmentFileFeedItem,
  ShipmentNoteFeedItem,
  ShipmentPaymentFeedItem,
  ShipmentSmsFeedItem,
  ShipmentTaskFeedItem,
} from '#/entities/shipment'
import { formatBytes, formatLongDate, formatTime } from '#/shared/lib/format'
import {
  CheckRoundedIcon,
  ContractIcon,
  EditIcon,
  EmailIcon,
  FileIcon,
  HistoryIcon,
  PaymentIcon,
  PhoneChatIcon,
} from '#/shared/ui/icons'

export type FeedEntryTone = 'neutral' | 'comms' | 'task' | 'payment'
export type FeedDetailTone = 'default' | 'income' | 'expense'

export interface FeedEntryMetaRow {
  label: string
  value: string
  tone?: FeedDetailTone
}

export interface FeedEntryFile {
  id: string
  name: string
  meta: string
  url: string
}

export interface FeedEntryExpanded {
  meta: FeedEntryMetaRow[]
  note?: string
  noteRich?: boolean
  file?: FeedEntryFile
}

export interface SidebarFeedEntryData {
  id: string
  kind: string
  icon: React.ComponentType<{ className?: string }>
  tone: FeedEntryTone
  actor: string
  action: string
  tag?: string
  time: string
  detail: string
  detailTone: FeedDetailTone
  detailRich?: boolean
  expanded?: FeedEntryExpanded
  occurredAt: string
}

const INBOUND = 'inbound'
const COMPLETED_TASK_STATUSES: ReadonlySet<string> = new Set([
  'completed',
  'archived',
])

function capitalize(value: string): string {
  return value ? value[0].toUpperCase() + value.slice(1) : value
}

function actorOrSystem(t: TFunction, actor: string | undefined): string {
  return actor && actor.length > 0 ? actor : t('detailSidebar.feed.system')
}

export function noteEntry(
  t: TFunction,
  note: ShipmentNoteFeedItem,
): SidebarFeedEntryData {
  return {
    id: note.id,
    kind: 'note',
    icon: EditIcon,
    tone: 'neutral',
    actor: actorOrSystem(t, note.author),
    action: t('detailSidebar.feed.addedNote'),
    time: formatTime(note.occurredAt),
    detail: note.text,
    detailTone: 'default',
    detailRich: true,
    occurredAt: note.occurredAt,
  }
}

function taskExpanded(
  t: TFunction,
  task: ShipmentTaskFeedItem,
): FeedEntryExpanded {
  const meta: FeedEntryMetaRow[] = [
    {
      label: t('detailSidebar.feed.taskMeta.status'),
      value: capitalize(task.status),
    },
  ]
  if (task.date) {
    meta.push({
      label: t('detailSidebar.feed.taskMeta.due'),
      value: formatLongDate(task.date),
    })
  }
  meta.push({
    label: t('detailSidebar.feed.taskMeta.priority'),
    value: capitalize(task.priority),
  })
  const assignee = task.assignees.join(', ') || task.author
  if (assignee) {
    meta.push({
      label: t('detailSidebar.feed.taskMeta.assignee'),
      value: assignee,
    })
  }
  return { meta }
}

export function taskEntry(
  t: TFunction,
  task: ShipmentTaskFeedItem,
): SidebarFeedEntryData {
  const completed = COMPLETED_TASK_STATUSES.has(task.status)
  return {
    id: task.id,
    kind: 'task',
    icon: CheckRoundedIcon,
    tone: 'task',
    actor: actorOrSystem(t, task.author),
    action: t(
      completed
        ? 'detailSidebar.feed.completedTask'
        : 'detailSidebar.feed.createdTask',
    ),
    time: formatTime(task.occurredAt),
    detail: task.title,
    detailTone: 'default',
    expanded: taskExpanded(t, task),
    occurredAt: task.occurredAt,
  }
}

export function smsEntry(
  t: TFunction,
  message: ShipmentSmsFeedItem,
): SidebarFeedEntryData {
  const inbound = message.direction === INBOUND
  return {
    id: message.id,
    kind: 'sms',
    icon: PhoneChatIcon,
    tone: 'comms',
    actor: actorOrSystem(t, message.fromPhoneDisplay),
    action: t(
      inbound ? 'detailSidebar.feed.repliedSms' : 'detailSidebar.feed.sentSms',
    ),
    tag: inbound ? t('detailSidebar.feed.tagInbound') : undefined,
    time: formatTime(message.occurredAt),
    detail: message.body ? `“${message.body}”` : '',
    detailTone: 'default',
    occurredAt: message.occurredAt,
  }
}

function emailExpanded(
  t: TFunction,
  email: ShipmentEmailFeedItem,
): FeedEntryExpanded | undefined {
  const meta: FeedEntryMetaRow[] = []
  if (email.recipient) {
    meta.push({
      label: t('detailSidebar.feed.emailMeta.to'),
      value: email.recipient,
    })
  }
  if (email.sender) {
    meta.push({
      label: t('detailSidebar.feed.emailMeta.from'),
      value: email.sender,
    })
  }
  if (meta.length === 0 && !email.body) return undefined
  return { meta, note: email.body || undefined, noteRich: true }
}

export function emailEntry(
  t: TFunction,
  email: ShipmentEmailFeedItem,
): SidebarFeedEntryData {
  const inbound = email.direction === INBOUND
  return {
    id: email.id,
    kind: 'email',
    icon: EmailIcon,
    tone: 'comms',
    actor: actorOrSystem(t, email.sender),
    action: t(
      inbound
        ? 'detailSidebar.feed.receivedEmail'
        : 'detailSidebar.feed.sentEmail',
    ),
    tag: email.isApp ? t('detailSidebar.feed.tagAutomation') : undefined,
    time: formatTime(email.occurredAt),
    detail: t('detailSidebar.feed.emailSubject', { subject: email.subject }),
    detailTone: 'default',
    expanded: emailExpanded(t, email),
    occurredAt: email.occurredAt,
  }
}

function fileMeta(file: ShipmentFileFeedItem): string {
  const parts: string[] = []
  if (file.sizeBytes) parts.push(formatBytes(file.sizeBytes))
  const ext = file.name.includes('.') ? file.name.split('.').pop() : undefined
  if (ext) parts.push(ext.toUpperCase())
  return parts.join(' · ')
}

export function fileEntry(
  t: TFunction,
  file: ShipmentFileFeedItem,
): SidebarFeedEntryData {
  const size = file.sizeBytes ? ` · ${formatBytes(file.sizeBytes)}` : ''
  return {
    id: file.id,
    kind: 'file',
    icon: FileIcon,
    tone: 'neutral',
    actor: actorOrSystem(t, file.authorName),
    action: t('detailSidebar.feed.uploadedFile'),
    time: formatTime(file.createdAt),
    detail: `${file.name}${size}`,
    detailTone: 'default',
    expanded: {
      meta: [],
      file: {
        id: file.id,
        name: file.name,
        meta: fileMeta(file),
        url: file.url,
      },
    },
    occurredAt: file.createdAt,
  }
}

function paymentMethodLabel(
  payment: ShipmentPaymentFeedItem,
): string | undefined {
  const parts: string[] = []
  if (payment.paymentType) parts.push(capitalize(payment.paymentType))
  if (payment.cardLast4) parts.push(`••${payment.cardLast4}`)
  return parts.length > 0 ? parts.join(' ') : undefined
}

function paymentExpanded(
  t: TFunction,
  payment: ShipmentPaymentFeedItem,
  income: boolean,
): FeedEntryExpanded | undefined {
  const meta: FeedEntryMetaRow[] = []
  const method = paymentMethodLabel(payment)
  if (method) {
    meta.push({
      label: t('detailSidebar.feed.paymentMeta.method'),
      value: method,
    })
  }
  if (payment.status) {
    meta.push({
      label: t('detailSidebar.feed.paymentMeta.status'),
      value: capitalize(payment.status),
      tone: income ? 'income' : 'expense',
    })
  }
  if (payment.reference) {
    meta.push({
      label: t('detailSidebar.feed.paymentMeta.reference'),
      value: payment.reference,
    })
  }
  return meta.length > 0 ? { meta } : undefined
}

export function paymentEntry(
  t: TFunction,
  payment: ShipmentPaymentFeedItem,
): SidebarFeedEntryData {
  const income = payment.direction !== 'expense'
  const directionLabel = t(
    income ? 'detailSidebar.feed.income' : 'detailSidebar.feed.expense',
  )
  const status = payment.status ? ` ${payment.status.toLowerCase()}` : ''
  return {
    id: payment.id,
    kind: 'payment',
    icon: PaymentIcon,
    tone: 'payment',
    actor: actorOrSystem(t, payment.customerName),
    action: t('detailSidebar.feed.recordedPayment'),
    tag: payment.isApp ? t('detailSidebar.feed.tagAutomation') : undefined,
    time: formatTime(payment.occurredAt),
    detail: `${directionLabel} · ${payment.amount}${status}`,
    detailTone: income ? 'income' : 'expense',
    expanded: paymentExpanded(t, payment, income),
    occurredAt: payment.occurredAt,
  }
}

export function contractEntry(
  t: TFunction,
  contract: ShipmentContractFeedItem,
): SidebarFeedEntryData {
  return {
    id: contract.id,
    kind: 'contract',
    icon: ContractIcon,
    tone: 'neutral',
    actor: actorOrSystem(t, contract.recipient),
    action: t('detailSidebar.feed.sentContract'),
    time: formatTime(contract.sentAt),
    detail: `${contract.contractType} · ${capitalize(contract.status)}`,
    detailTone: 'default',
    occurredAt: contract.sentAt,
  }
}

export function activityEntry(
  t: TFunction,
  activity: ShipmentActivityFeedItem,
): SidebarFeedEntryData {
  return {
    id: activity.id,
    kind: 'activity',
    icon: HistoryIcon,
    tone: 'neutral',
    actor: t('detailSidebar.feed.system'),
    action: activity.text,
    time: formatTime(activity.occurredAt),
    detail: '',
    detailTone: 'default',
    occurredAt: activity.occurredAt,
  }
}

export function toSidebarFeedEntry(
  t: TFunction,
  item: ShipmentFeedItem,
): SidebarFeedEntryData {
  switch (item.kind) {
    case 'note':
      return noteEntry(t, item.data)
    case 'task':
      return taskEntry(t, item.data)
    case 'sms':
      return smsEntry(t, item.data)
    case 'email':
      return emailEntry(t, item.data)
    case 'file':
      return fileEntry(t, item.data)
    case 'payment':
      return paymentEntry(t, item.data)
    case 'contract':
      return contractEntry(t, item.data)
    default:
      return activityEntry(t, item.data)
  }
}
